import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { getFirstAccessiblePath } from '@/utils/navigation'

const routes: RouteRecordRaw[] = [
  // 测试页面
  {
    path: '/test-naive',
    name: 'TestNaive',
    component: () => import('@/views/TestNaiveUI.vue'),
    meta: { title: 'Naive UI 测试' }
  },
  // 认证页面（独立路由，不使用 AppLayout）
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginPage.vue'),
    meta: { title: '登录', fullscreen: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/RegisterPage.vue'),
    meta: { title: '注册', fullscreen: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordPage.vue'),
    meta: { title: '忘记密码', fullscreen: true }
  },
  // 分享详情（个人资源库内路由）
  {
    path: '/share/:id',
    name: 'ShareDetail',
    component: () => import('@/views/Share/ShareDetail.vue'),
    meta: { title: '分享详情' }
  },
  // 分享预览（独立路由，不使用 AppLayout）
  {
    path: '/s/:token',
    name: 'ShareView',
    component: () => import('@/views/Share/ShareView.vue'),
    meta: { title: '分享预览' }
  },
  // 保留旧的 auth 路由以向后兼容
  {
    path: '/auth',
    redirect: '/login'
  },
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    children: [
      {
        path: '',
        redirect: '/dashboard'
      },
      // 仪表盘
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '仪表盘', perm: 'dashboard:view' }
      },
      // 资源中心
      {
        path: 'public',
        name: 'PublicLibrary',
        component: () => import('@/views/PublicLibrary.vue'),
        meta: { title: '公共资源库' }
      },
      {
        path: 'personal',
        name: 'PersonalLibrary',
        component: () => import('@/views/PersonalLibrary.vue'),
        meta: { title: '个人资源库' }
      },
      {
        path: 'intelligent-search',
        name: 'IntelligentSearch',
        component: () => import('@/views/IntelligentSearch.vue'),
        meta: { title: '智能检索' }
      },
      {
        path: 'resource/:id',
        name: 'ResourceDetail',
        component: () => import('@/views/ResourceDetail.vue'),
        meta: { title: '资源详情' }
      },
      {
        path: 'recycle-bin',
        name: 'RecycleBin',
        component: () => import('@/views/RecycleBin.vue'),
        meta: { title: '回收站' }
      },
      // 协作中心
      {
        path: 'organization-members',
        name: 'OrganizationMembers',
        component: () => import('@/views/OrganizationMembers.vue'),
        meta: { title: '组织成员', perm: 'collaboration:view' }
      },
      {
        path: 'contacts',
        name: 'Contacts',
        component: () => import('@/views/Contacts.vue'),
        meta: { title: '联系人', perm: 'collaboration:view' }
      },
      {
        path: 'my-groups',
        name: 'MyGroups',
        component: () => import('@/views/MyGroups.vue'),
        meta: { title: '我的群组', perm: 'collaboration:view' }
      },
      {
        path: 'organization-list',
        name: 'OrganizationList',
        component: () => import('@/views/OrganizationList.vue'),
        meta: { title: '组织管理', perm: 'organization:list' }
      },
      {
        path: 'organization/:id',
        name: 'OrganizationDetail',
        component: () => import('@/views/OrganizationDetail.vue'),
        meta: { title: '组织详情', perm: 'organization:list' }
      },
      {
        path: 'organization-manage',
        name: 'OrganizationManage',
        redirect: '/organization-list',
        meta: { title: '组织管理', perm: 'organization:list' }
      },
      {
        path: 'member-manage',
        name: 'MemberManage',
        component: () => import('@/views/MemberManage.vue'),
        meta: { title: '成员管理', perm: 'user:list' }
      },
      {
        path: 'department-manage',
        name: 'DepartmentManage',
        component: () => import('@/views/DepartmentManage.vue'),
        meta: { title: '部门管理', perm: 'department:list' }
      },
      // 签发中心
      {
        path: 'issuance-task',
        name: 'IssuanceTask',
        component: () => import('@/views/IssuanceTask.vue'),
        meta: { title: '签发任务', perm: 'publish:view' }
      },
      {
        path: 'platform-management',
        name: 'PlatformManagement',
        component: () => import('@/views/PlatformManagement.vue'),
        meta: { title: '平台管理', perm: 'publish:view' }
      },
      {
        path: 'account-management',
        name: 'AccountManagement',
        component: () => import('@/views/AccountManagement.vue'),
        meta: { title: '账号管理', perm: 'publish:view' }
      },
      // 保留旧的签发中心路由（重定向到签发任务）
      {
        path: 'issuance-management',
        redirect: '/issuance-task'
      },
      // 内容安全中心
      {
        path: 'content-audit',
        name: 'ContentAudit',
        component: () => import('@/views/ContentAudit.vue'),
        meta: { title: '内容审核', perm: 'content:audit:list' }
      },
      {
        path: 'audit-rule-manage',
        name: 'AuditRuleManage',
        component: () => import('@/views/AuditRuleManage.vue'),
        meta: { title: '审核规则管理', perm: 'content:audit:list' }
      },
      {
        path: 'risk-search',
        name: 'RiskSearch',
        component: () => import('@/views/RiskSearch.vue'),
        meta: { title: '风险检索', perm: 'content:audit:list' }
      },
      {
        path: 'risk-detail/:id',
        name: 'RiskDetail',
        component: () => import('@/views/RiskDetail.vue'),
        meta: { title: '风险详情', perm: 'content:audit:list' }
      },
      {
        path: 'erase-tasks',
        name: 'EraseTaskList',
        component: () => import('@/views/EraseTaskList.vue'),
        meta: { title: '擦除任务管理', perm: 'content:audit:list' }
      },
      {
        path: 'erase-task/:id',
        name: 'EraseTaskDetail',
        component: () => import('@/views/EraseTaskDetail.vue'),
        meta: { title: '擦除任务详情', perm: 'content:audit:list' }
      },
      {
        path: 'violation-handle',
        redirect: '/risk-search'
      },
      {
        path: 'archive-manage',
        name: 'ArchiveManage',
        component: () => import('@/views/ArchiveManage.vue'),
        meta: { title: '封存管理', perm: 'content:audit:list' }
      },
      {
        path: 'violation-person-library',
        name: 'ViolationPersonLibrary',
        component: () => import('@/views/ViolationPersonLibrary.vue'),
        meta: { title: '违规人物库', perm: 'content:audit:list' }
      },
      // 保留旧路由（已废弃）
      {
        path: 'content-standards',
        redirect: '/content-audit'
      },
      {
        path: 'data-security',
        redirect: '/content-audit'
      },
      // 系统配置
      {
        path: 'config',
        name: 'SystemConfig',
        component: () => import('@/views/SystemConfig.vue'),
        meta: { title: '系统配置', perm: 'system:config:list' }
      },
      // 配置中心
      {
        path: 'configcenter/model-manage',
        name: 'AiModelManage',
        component: () => import('@/views/ConfigCenter/AiModelManage.vue'),
        meta: { title: 'AI模型管理', perm: 'system:config:list' }
      },
      {
        path: 'configcenter/usage-stats',
        name: 'AiUsageStats',
        component: () => import('@/views/ConfigCenter/AiUsageStats.vue'),
        meta: { title: 'AI用量统计', perm: 'system:config:list' }
      },
      {
        path: 'role-switcher',
        name: 'RoleSwitcher',
        component: () => import('@/views/RoleSwitcher.vue'),
        meta: { title: '权限测试工具' }
      },
      // 其他页面
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/Profile.vue'),
        meta: { title: '个人中心' }
      },
      {
        path: 'messages',
        name: 'MessageCenter',
        component: () => import('@/views/MessageCenter.vue'),
        meta: { title: '消息中心' }
      },
      {
        path: 'notifications',
        name: 'MyNotifications',
        component: () => import('@/views/MyNotifications.vue'),
        meta: { title: '我的通知' }
      },
      {
        path: 'cloudbrain',
        name: 'CloudBrain',
        component: () => import('@/views/CloudBrain.vue'),
        meta: { title: '云枢智脑', perm: 'ai:view' }
      },
      // 云枢智脑 - 智能体视图
      {
        path: 'cloudbrain/audio',
        name: 'AudioAgent',
        component: () => import('@/views/CloudBrain/AudioAgent.vue'),
        meta: { title: '音频智能体', perm: 'ai:view' }
      },
      {
        path: 'cloudbrain/image',
        name: 'ImageAgent',
        component: () => import('@/views/CloudBrain/ImageAgent.vue'),
        meta: { title: '图片智能体', perm: 'ai:view' }
      },
      {
        path: 'cloudbrain/document',
        name: 'DocumentAgent',
        component: () => import('@/views/CloudBrain/DocumentAgent.vue'),
        meta: { title: '文档智能体', perm: 'ai:view' }
      },
      {
        path: 'cloudbrain/cad',
        name: 'CADAgent',
        component: () => import('@/views/CloudBrain/CADAgent.vue'),
        meta: { title: 'CAD智能体', perm: 'ai:view' }
      },
      {
        path: 'cloudbrain/video',
        name: 'VideoAgent',
        component: () => import('@/views/CloudBrain/VideoAgent.vue'),
        meta: { title: '视频智能体', perm: 'ai:view' }
      },
      {
        path: 'cloudbrain/ppt',
        name: 'PPTAgent',
        component: () => import('@/views/CloudBrain/PPTAgent.vue'),
        meta: { title: 'PPT智能体', perm: 'ai:view' }
      },
      {
        path: 'cloudbrain/manage',
        name: 'AgentManage',
        component: () => import('@/views/CloudBrain/AgentManage.vue'),
        meta: { title: '智能体管理', perm: 'ai:view' }
      },
      // 保留旧路由以向后兼容
      {
        path: 'publish-flow-demo',
        name: 'PublishFlowDemo',
        component: () => import('@/views/PublishFlowDemo.vue'),
        meta: { title: '签发流程演示' }
      },
      {
        path: 'share-preview-demo',
        name: 'SharePreviewDemo',
        component: () => import('@/views/SharePreviewDemo.vue'),
        meta: { title: '分享预览演示' }
      },
      // 资源操作演示
      {
        path: 'resource-operations-demo',
        name: 'ResourceOperationsDemo',
        component: () => import('@/views/ResourceOperationsDemo.vue'),
        meta: { title: '资源操作演示' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_ROUTER_BASE || '/'),
  routes
})

// 路由守卫 - 检查登录状态
router.beforeEach(async (to, _from, next) => {
  const token = localStorage.getItem('token')
  const isAuthPage = ['/login', '/register', '/forgot-password', '/auth'].includes(to.path)

  // 如果已登录且访问认证页面，重定向到第一个有权限页面
  if (token && isAuthPage) {
    next('/dashboard')
    return
  }

  // 如果未登录且访问需要认证的页面，重定向到登录页
  if (!token && !isAuthPage && to.path !== '/test-naive') {
    next('/login')
    return
  }

  // 如果已登录但没有用户信息，尝试获取用户信息
  if (token && !isAuthPage) {
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()

    if (!authStore.userInfo) {
      try {
        await authStore.fetchUserInfo()
      } catch (error) {
        console.error('获取用户信息失败:', error)
        // 获取用户信息失败，清除 token 并跳转到登录页
        authStore.resetState()
        next('/login')
        return
      }
    }

    // 加载最新权限码用于路由权限校验
    await authStore.fetchPermissions()

    // 根路径：重定向到第一个有权限的页面（不再固定跳 /dashboard）
    if (to.path === '/') {
      next(getFirstAccessiblePath(authStore))
      return
    }

    // 路由权限校验：无权限则跳转到第一个可访问页面（避免空白页）
    const requiredPerm = to.meta.perm as string | undefined
    if (requiredPerm && !authStore.hasPermission(requiredPerm)) {
      const fallback = getFirstAccessiblePath(authStore)
      if (to.path !== fallback) {
        window.$message?.warning?.('暂无该权限，已跳转到可访问页面')
        next(fallback)
      } else {
        next(false)
      }
      return
    }
  }

  // 其他情况正常访问
  next()
})

export default router
